import React, { useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { FaInfoCircle } from "react-icons/fa";
import Button from "../Button";
import { Modal } from "../Modal";

/**
 * InfoButton: a small (i) that opens a short explanation in a modal.
 *
 * For a panel that should present as the minimum to scan and share, with the explanation there
 * when it is wanted (Lee, 12 Oct 2026). The words are not in the page until the button is
 * pressed, and the modal is the shared one, so it closes on Escape and on a click outside.
 *
 * The modal is drawn at the END OF THE PAGE, not inside whatever the button sits in: a panel
 * that dims itself while it loads, or a heading in capitals, would otherwise dim or capitalise
 * the explanation too.
 *
 * ⚠️ What a person NEEDS in order to read the figures rightly (a warning, a failed check, "too
 * few to tell") stays on the face of the panel, in a few words. This is for the explanation.
 *
 * @param title     the modal's heading, and what the button is called for a screen reader
 * @param children  the explanation
 * @param text      optional words beside the icon (e.g. "Details")
 */
const InfoButton = ({ title, children, text, size = "medium", className = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="small"
        icon={<FaInfoCircle />}
        aria-label={text ? undefined : `About: ${title}`}
        aria-haspopup="dialog"
        className={className}
        onClick={() => setOpen(true)}
      >
        {text}
      </Button>
      {open && createPortal(
        <Modal isOpen onClose={() => setOpen(false)} title={title} size={size}>
          {children}
        </Modal>,
        document.body,
      )}
    </>
  );
};

InfoButton.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  text: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  className: PropTypes.string,
};

export default InfoButton;
